import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  X,
  Search,
  Filter,
  Trash2,
  Loader2,
  PackageX,
  CheckCircle2,
  AlertCircle,
  ArrowDownLeft,
  Package,
  Power,
  PowerOff,
} from "lucide-react";
import {
  fetchAgentProducts,
  deleteAgentProducts,
  toggleAgentActive,
  clearProducts,
  resetPagination,
} from "../../../RTK/agents/AgentProducts";
import { toast } from "react-toastify";

const AgentProductsPopup = ({ agentId, agentName, onClose }) => {
  const dispatch = useDispatch();

  const {
    products,
    agent,
    count,
    next,
    loading,
    deleteLoading,
    toggleLoading,
    error,
    hasMore,
    currentPage,
    totalPages,
  } = useSelector((state) => state.agentProducts || {});

  const [search, setSearch] = useState("");
  const [productType, setProductType] = useState("");
  const [status, setStatus] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // ✅ Ref for Infinite Scroll
  const loadMoreRef = useRef(null);

  // ✅ Initial load
  useEffect(() => {
    console.log("🎭 AgentProductsPopup mounted");
    console.log("🆔 Agent ID:", agentId);
    console.log("📝 Agent Name:", agentName);

    if (!agentId) {
      console.error("❌ No agent ID provided!");
      toast.error("معرف الوكيل مفقود");
      return;
    }

    // Clear previous data
    dispatch(clearProducts());

    // Fetch initial products
    dispatch(
      fetchAgentProducts({
        agentId,
        page: 1,
        search: "",
        productType: "",
        status: "",
        append: false,
      })
    );

    return () => {
      dispatch(clearProducts());
    };
  }, [agentId, dispatch, agentName]);

  // ✅ Display error
  useEffect(() => {
    if (error) {
      console.error("❌ Error:", error);
      toast.error(
        `فشل في تحميل المنتجات: ${typeof error === "string" ? error : JSON.stringify(error)}`
      );
    }
  }, [error]);

  // ✅ Infinite Scroll Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && next) {
          console.log("🔄 Loading more products...", {
            currentPage,
            totalPages,
            hasMore,
          });

          dispatch(
            fetchAgentProducts({
              agentId,
              page: currentPage + 1,
              search,
              productType,
              status,
              append: true,
            })
          );
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px",
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [
    dispatch,
    agentId,
    hasMore,
    loading,
    next,
    currentPage,
    totalPages,
    search,
    productType,
    status,
  ]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  const handleApplyFilters = () => {
    dispatch(resetPagination());
    dispatch(
      fetchAgentProducts({
        agentId,
        page: 1,
        search,
        productType,
        status,
        append: false,
      })
    );
  };

  const handleToggleAgent = async () => {
    if (agent) {
      await dispatch(
        toggleAgentActive({
          agentId,
          isActive: !agent.is_active,
        })
      );
      dispatch(resetPagination());
      dispatch(
        fetchAgentProducts({
          agentId,
          page: 1,
          search,
          productType,
          status,
          append: false,
        })
      );
    }
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map((p) => p.id));
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleDeleteProducts = async () => {
    if (selectedProducts.length === 0) return;

    await dispatch(
      deleteAgentProducts({
        agentId,
        productIds: selectedProducts,
      })
    );

    setSelectedProducts([]);
    setShowDeleteConfirm(false);
    dispatch(resetPagination());

    dispatch(
      fetchAgentProducts({
        agentId,
        page: 1,
        search,
        productType,
        status,
        append: false,
      })
    );
  };

  const StatusBadge = ({ status }) => {
    const colors = {
      available: "bg-green-500/20 text-green-400 border-green-500/30",
      unavailable: "bg-red-500/20 text-red-400 border-red-500/30",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium border ${
          colors[status] || "bg-gray-500/20 text-gray-400 border-gray-500/30"
        }`}
      >
        {status === "available" ? "متاح" : "غير متاح"}
      </span>
    );
  };

  const TypeBadge = ({ type }) => {
    const typeLabels = {
      quantity: "كمية",
      package: "باقة",
      stock: "مخزون",
    };

    const typeColors = {
      quantity: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      package: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      stock: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium border ${
          typeColors[type] || "bg-gray-500/20 text-gray-400 border-gray-500/30"
        }`}
      >
        {typeLabels[type] || type}
      </span>
    );
  };

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center z-[6000] bg-black/90 backdrop-blur-sm">
        <div
          className={`${
            isClosing ? "animate-close" : "animate-popup"
          } bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl w-[95%] max-w-7xl border border-slate-800 shadow-2xl z-[6100] max-h-[90vh] overflow-hidden flex flex-col`}
        >
          {/* Header */}
          <div className="p-4 md:p-6 border-b border-slate-800 bg-gradient-to-r from-blue-900/20 to-purple-900/20">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="p-2 bg-blue-500/20 rounded-lg flex-shrink-0">
                  <Package className="w-5 h-5 md:w-6 md:h-6 text-blue-400" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl md:text-2xl font-bold text-white truncate flex items-center gap-2 flex-wrap">
                    <span className="truncate">{agentName}</span>
                    {agent?.is_active ? (
                      <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-green-400 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 flex-shrink-0" />
                    )}
                  </h2>
                  <p className="text-slate-400 text-xs md:text-sm truncate">
                    معرف الوكيل: #{agentId}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleToggleAgent}
                  disabled={toggleLoading}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm ${
                    agent?.is_active
                      ? "bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
                      : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                  } disabled:opacity-50`}
                  title={agent?.is_active ? "تعطيل الوكيل" : "تفعيل الوكيل"}
                >
                  {toggleLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : agent?.is_active ? (
                    <PowerOff className="w-4 h-4" />
                  ) : (
                    <Power className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">
                    {agent?.is_active ? "تعطيل" : "تفعيل"}
                  </span>
                </button>

                {selectedProducts.length > 0 && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center gap-2 px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="hidden sm:inline">حذف</span>
                    <span>({selectedProducts.length})</span>
                  </button>
                )}

                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-red-500/20 rounded-lg transition-colors group"
                >
                  <X className="w-5 h-5 text-slate-400 group-hover:text-red-400" />
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 md:p-6 border-b border-slate-800 bg-slate-900/50">
            <div className="flex items-center gap-3 mb-4">
              <Filter className="w-5 h-5 text-blue-400 flex-shrink-0" />
              <h3 className="text-base md:text-lg font-bold text-white">
                تصفية المنتجات
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="md:col-span-2 lg:col-span-2">
                <label className="block text-sm text-slate-400 mb-2">
                  البحث بالاسم
                </label>
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="ابحث عن منتج..."
                    className="w-full bg-slate-800 text-white pr-10 px-3 py-2 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm"
                    dir="rtl"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  نوع المنتج
                </label>
                <select
                  value={productType}
                  onChange={(e) => setProductType(e.target.value)}
                  className="w-full bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm"
                >
                  <option value="">الكل</option>
                  <option value="quantity">كمية</option>
                  <option value="package">باقة</option>
                  <option value="stock">مخزون</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  الحالة
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full bg-slate-800 text-white px-3 py-2 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm"
                >
                  <option value="">الكل</option>
                  <option value="available">متاح</option>
                  <option value="unavailable">غير متاح</option>
                </select>
              </div>
            </div>

            <button
              onClick={handleApplyFilters}
              disabled={loading && products.length === 0}
              className="mt-3 w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all text-sm disabled:opacity-50"
            >
              {loading && products.length === 0 ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Filter className="w-4 h-4" />
                  تطبيق الفلتر
                </>
              )}
            </button>
          </div>

          {/* Products Count & Select All */}
          {products.length > 0 && (
            <div className="px-4 md:px-6 py-3 border-b border-slate-800 bg-slate-900/30 flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-slate-400">
                إجمالي المنتجات:{" "}
                <span className="text-white font-bold">{count}</span>
              </p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedProducts.length === products.length}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-slate-700 bg-slate-800 checked:bg-blue-500 cursor-pointer"
                />
                <span className="text-sm text-slate-400">تحديد الكل</span>
              </label>
            </div>
          )}

          {/* Products List */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-900/30">
            {loading && products.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                  <p className="text-slate-400">جاري تحميل المنتجات...</p>
                </div>
              </div>
            ) : error && products.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <p className="text-red-400 font-medium mb-2">
                    حدث خطأ أثناء تحميل المنتجات
                  </p>
                  <p className="text-slate-400 text-sm mb-4">
                    {typeof error === "string"
                      ? error
                      : JSON.stringify(error, null, 2)}
                  </p>
                  <button
                    onClick={() =>
                      dispatch(
                        fetchAgentProducts({
                          agentId,
                          page: 1,
                          search: "",
                          productType: "",
                          status: "",
                          append: false,
                        })
                      )
                    }
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm"
                  >
                    إعادة المحاولة
                  </button>
                </div>
              </div>
            ) : products.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <PackageX className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-lg text-slate-400">لا توجد منتجات</p>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4 mb-6">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className={`bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border p-3 md:p-4 transition-all ${
                        selectedProducts.includes(product.id)
                          ? "border-blue-500 shadow-lg shadow-blue-500/20"
                          : "border-slate-700 hover:border-slate-600"
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(product.id)}
                          onChange={() => handleSelectProduct(product.id)}
                          className="w-4 h-4 rounded border-slate-700 bg-slate-800 checked:bg-blue-500 cursor-pointer flex-shrink-0"
                        />
                        <div className="flex gap-2 flex-wrap justify-end">
                          <StatusBadge status={product.product_status} />
                          <TypeBadge type={product.product_type} />
                        </div>
                      </div>

                      <h3 className="text-base md:text-lg font-bold text-white mb-2 truncate">
                        {product.product_name}
                      </h3>

                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-slate-400">معرف المنتج:</span>
                          <span className="text-white font-medium">
                            #{product.product_id}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-slate-400">السعر الحالي:</span>
                          <span className="text-green-400 font-bold">
                            ${product.current_price}
                          </span>
                        </div>

                        {product.agent_price && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">سعر الوكيل:</span>
                            <span className="text-blue-400 font-bold">
                              ${parseFloat(product.agent_price).toFixed(4)}
                            </span>
                          </div>
                        )}

                        {product.no_active_agent && (
                          <div className="flex items-center gap-2 text-yellow-400 bg-yellow-500/10 p-2 rounded-lg">
                            <AlertCircle className="w-3 h-3 flex-shrink-0" />
                            <span className="text-xs">لا يوجد وكيل نشط</span>
                          </div>
                        )}

                        <div className="pt-2 border-t border-slate-700 text-xs text-slate-500">
                          {new Date(
                            product.product_created_at
                          ).toLocaleDateString("ar-EG")}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Infinite Scroll Loader */}
                {hasMore && (
                  <div
                    ref={loadMoreRef}
                    className="py-6 flex flex-col items-center justify-center gap-2"
                  >
                    {loading ? (
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
                        <span className="text-slate-400 text-sm">
                          جاري تحميل المزيد...
                        </span>
                      </div>
                    ) : (
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2 text-slate-400 text-sm mb-2 animate-pulse">
                          <ArrowDownLeft className="w-4 h-4" />
                          <span>قم بالتمرير لتحميل المزيد</span>
                        </div>
                        <div className="text-xs text-slate-500">
                          صفحة {currentPage} من {totalPages}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* End Message */}
                {!loading && !hasMore && products.length > 0 && (
                  <p className="text-center text-slate-400 py-4 border-t border-slate-700 flex items-center justify-center gap-2 text-sm">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    تم تحميل جميع المنتجات ({count} منتج)
                  </p>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {products.length > 0 && (
            <div className="p-4 md:p-6 border-t border-slate-800 bg-slate-900/50 flex items-center justify-between flex-wrap gap-3">
              <p className="text-slate-400 text-sm">
                عرض {products.length} من {count} منتج
                {totalPages > 1 && ` (صفحة ${currentPage} من ${totalPages})`}
              </p>
              <button
                onClick={handleClose}
                className="px-4 md:px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
              >
                إغلاق
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[7000] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-slate-700 p-6 max-w-md w-full">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-red-500/20 rounded-xl flex-shrink-0">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">تأكيد الحذف</h3>
                <p className="text-sm text-slate-400">
                  هل أنت متأكد من حذف {selectedProducts.length} منتج؟
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleDeleteProducts}
                disabled={deleteLoading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all disabled:opacity-50 text-sm"
              >
                {deleteLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    جاري الحذف...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" />
                    حذف
                  </>
                )}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-all disabled:opacity-50 text-sm"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgentProductsPopup;